import { useParams } from 'react-router-dom';
import {
  useGetProjectByIdQuery,
  useLazyGetProjectIntegrationsListQuery,
  useLazyGetZendeskConsentLinkQuery,
  useUpdateProjectMutation,
  useZendeskChatIntegrationAutoSetupMutation,
} from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, numberScheme, stringScheme } from 'src/utils/validation-schemes';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { EditZendeskChatWidgetSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskChatIntegrationSettings/EditZendeskChatWidgetSettings/EditZendeskChatWidgetSettings';
import { EditZendeskChatAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskChatIntegrationSettings/EditZendeskChatAdvancedSettings/EditZendeskChatAdvancedSettings';
import { EditZendeskChatApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskChatIntegrationSettings/EditZendeskChatApiDetailsSettings/EditZendeskChatApiDetailsSettings';
import moment from 'moment/moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { useDispatch } from 'react-redux';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useNotification } from 'src/hooks/useNotification';
import { AUTH_STATUS_POLL_INTERVAL_MS, AUTH_STATUS_POLL_TIMEOUT_MS } from 'src/shared/constants';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const AccordionIcon = ({ open }) => (
  <svg
    className={clsx('transition-transform', open && 'rotate-180')}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M12.6523 0.651864L6.35244 6.65186L0.652531 0.65187"
      stroke="#7A6BE5"
      strokeWidth="1.79997"
    />
  </svg>
);

export const EditZendeskChatIntegrationSettings = ({ disabled = false, integration, onSubmit }) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const loader = useLoader();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const duration = currentProject?.autoclose_time_window_minutes
    ? moment.duration(currentProject?.autoclose_time_window_minutes, 'minutes')
    : 0;
  const autocloseHours = duration ? Math.floor(duration.asHours()) : 0;
  const autocloseMinutes = duration ? duration.minutes() : 0;
  const [updateProject, { isLoading: isLoadingUpdateProject }] = useUpdateProjectMutation();
  const [setZendeskChatAutoSetup, { isLoading: isLoadingSetZendeskChatAutoSetup }] =
    useZendeskChatIntegrationAutoSetupMutation();
  const dispatch = useDispatch();
  const notification = useNotification();
  const isAuthorized = integration?.integration_data?.auth_status === 'ok';
  const isZendeskOAuthUsed = integration?.integration_data?.auth_type === 'oauth';
  const [getZendeskConsentLinkQuery] = useLazyGetZendeskConsentLinkQuery();
  const [getProjectIntegrationsList] = useLazyGetProjectIntegrationsListQuery();
  const [isCheckingAuthStatus, setIsCheckingAuthStatus] = useState(false);
  const isMountedRef = useRef(true);
  const isPollingAuthStatusRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isLoadingUpdateProject || isLoadingSetZendeskChatAutoSetup) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateProject, isLoadingSetZendeskChatAutoSetup]);

  const methods = useForm<any>({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain || '',
      author_display_name: integration?.integration_data?.author_display_name || '',
      author_avatar_url: integration?.integration_data?.author_avatar_url || '',
      app_id: integration?.integration_data?.app_id || '',
      conversation_key_id: integration?.integration_data?.conversation_key_id || '',
      welcome_message: integration?.integration_data?.welcome_message || '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
      responder_mode: integration?.integration_data?.responder_mode || 'default_responder',
      flow_builder: integration?.integration_data?.responder_mode
        ? integration?.integration_data?.responder_mode !== 'default_responder'
        : false,
      widget_integration_id: '',
      conversation_secret_key: '',
    },
    resolver: yupResolver(
      object({
        subdomain: stringScheme({ required: false }).label('Zendesk subdomain'),
        author_display_name: stringScheme({ required: false }).label('Author display name'),
        author_avatar_url: stringScheme({ required: false }).label('Author avatar url').nullable(),
        app_id: stringScheme({ required: false }).label('App ID'),
        conversation_key_id: stringScheme({ required: false }).label('Key ID'),
        conversation_secret_key: stringScheme({ required: false }).label('Secret key'),
        welcome_message: stringScheme({ required: false }).label('AI Welcome message'),
        widget_integration_id: stringScheme({ required: false }).label('Widget integration id'),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
        responder_mode: stringScheme({ required: true }).label('Responder Mode'),
        flow_builder: booleanScheme({ required: true }),
      }),
    ),
  });

  // Polls the project integrations until the Zendesk OAuth consent is granted (auth_status "ok").
  // Resolves with the authorized integration, or null on timeout or when the form unmounts.
  const waitForZendeskAuthorization = async (integrationId: number) => {
    const deadline = Date.now() + AUTH_STATUS_POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await wait(AUTH_STATUS_POLL_INTERVAL_MS);
      if (!isMountedRef.current) return null;

      const { data: integrations } = await getProjectIntegrationsList({
        project_id: id,
        skip_dispatch: true,
      });
      if (!isMountedRef.current) return null;

      const currentIntegration = integrations?.find(({ id }) => id === integrationId);
      if (currentIntegration?.integration_data?.auth_status === 'ok') {
        return currentIntegration;
      }
    }

    return null;
  };

  const pollZendeskAuthorization = async (integrationId: number) => {
    if (isPollingAuthStatusRef.current) return;
    isPollingAuthStatusRef.current = true;
    setIsCheckingAuthStatus(true);

    const authorizedIntegration = await waitForZendeskAuthorization(integrationId);
    isPollingAuthStatusRef.current = false;

    if (!isMountedRef.current) return;
    setIsCheckingAuthStatus(false);

    if (authorizedIntegration) {
      dispatch(setSelectedIntegration({ ...integration, ...authorizedIntegration }));
      notification.success('Zendesk account was connected.');
    } else {
      notification.error('Zendesk authorization timed out. Please try again.');
    }
  };

  const openZendeskConsentPage = async () => {
    const { data } = await getZendeskConsentLinkQuery({
      project_id: id,
      integration_id: integration.id,
      type: 'zendesk-chat',
    });
    if (!data?.authorize_url) return false;

    window.open(data.authorize_url);
    return true;
  };

  const onConnectWithZendesk = async () => {
    if (integration?.id) {
      if (await openZendeskConsentPage()) {
        pollZendeskAuthorization(integration.id);
      }
    }
  };

  // The integration is already authorized, so there is no status change to poll for.
  const onReconnectWithZendesk = async () => {
    if (integration?.id) {
      await openZendeskConsentPage();
    }
  };

  // Runs the same validation as submit; the connect handlers only fire for a valid form.
  const connectWithZendesk = methods.handleSubmit(onConnectWithZendesk);
  const reconnectWithZendesk = methods.handleSubmit(onReconnectWithZendesk);

  const submit = async (data) => {
    if (data.widget_integration_id) {
      await setZendeskChatAutoSetup({
        projectId: id,
        integrationId: integration.id,
        body: {
          integrationId: integration.id,
          widget_integration_id: data.widget_integration_id,
          responder_mode: data.flow_builder ? 'handoff' : 'default_responder',
        },
      });
    }
    delete data.widget_integration_id;
    delete data.flow_builder;
    delete data.responder_mode;

    const autocloseMinutes = moment
      .duration(data.autocloseHours, 'hours')
      .add(data.autocloseMinutes, 'minutes')
      .asMinutes();

    const isAutocloseChanged = currentProject?.autoclose_time_window_minutes !== autocloseMinutes;

    if (isAutocloseChanged) {
      await updateProject({
        projectId: currentProject.id,
        name: currentProject?.name,
        rate_limit_project: currentProject?.rate_limit_project,
        rate_limit_project_per: currentProject?.rate_limit_project_per,
        rate_limit_chat: currentProject?.rate_limit_chat,
        rate_limit_chat_per: currentProject?.rate_limit_chat_per,
        rate_limit_max_chats: currentProject?.rate_limit_max_chats,
        rate_limit_max_chats_per: currentProject?.rate_limit_max_chats_per,
        cocbi_project_id: currentProject?.cocbi_project_id,
        use_cocbi_model: currentProject?.use_cocbi_model,
        ai_reply_generation: currentProject?.ai_reply_generation,
        ai_text_lense: currentProject?.ai_text_lense,
        autoclose_time_window_minutes: autocloseMinutes ? autocloseMinutes : null,
      });
    }
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;
    delete data.conversation_secret_key;

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
        subdomain: getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain,
        project_name: currentProject.name,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="web-widget-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Web Widget settings</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('web-widget-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskChatWidgetSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskChatAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskChatApiDetailsSettings
                isAuthorized={isAuthorized}
                isZendeskOAuthUsed={isZendeskOAuthUsed}
                isCheckingAuthStatus={isCheckingAuthStatus}
                onConnectWithZendesk={connectWithZendesk}
                onReconnectWithZendesk={reconnectWithZendesk}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
